import { HttpClient, HttpStatusCode } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { API_URL, APP_CONFIG } from '@shared/config';
import {
  BackgroundAssetMode,
  BackgroundAnimationAsset,
  BackgroundAnimationImageType,
} from '@shared/models/background-animation.model';
import {
  BackgroundAsset,
  BackgroundAssetType,
  NONE_BACKGROUND_ASSET,
} from '@shared/components/background/background.model';
import { THEMES } from '@shared/config/themes';
import { OD_BACKEND_ROUTES } from 'src/app/od-backend/config/od-backend.routes';
import { Observable, of, throwError } from 'rxjs';
import { catchError, map, shareReplay, switchMap } from 'rxjs/operators';
import { FileUrlService } from '@shared/services/file-url.service';
import {
  BrandingFileRole,
  FileEntity,
  FileKind,
} from '@shared/models/file.model';
import { HttpErrorSkip } from '../../interceptors/http-error/http-error-skip.model';

interface BackgroundAnimationResponse {
  html?: string | null;
  css?: string | null;
  hasImageAsset?: boolean | null;
  hasAnimationAsset?: boolean | null;
  mode?: BackgroundAssetMode | null;
}

interface BackgroundAnimationAssetStateResponse {
  hasImageAsset: boolean;
  hasAnimationAsset: boolean;
  mode: BackgroundAssetMode;
  animation: BackgroundAnimationAsset | null;
}

interface BrandingFileMetadataResponse {
  kind?: FileKind | null;
}

interface BackgroundFileResponse {
  fileId: string;
  kind: FileKind;
  theme: string;
  status?: string;
}

@Injectable({
  providedIn: 'root',
})
export class BackgroundAnimationService {
  private readonly httpClient = inject(HttpClient);
  private readonly fileUrlService = inject(FileUrlService);
  private readonly baseUrl = inject(APP_CONFIG).odBackendApiUrl;
  private readonly orApiUrl = inject(APP_CONFIG).orApiUrl;
  private readonly metadataCache = new Map<string, Observable<unknown>>();

  private getCached<T>(
    url: string,
    request: () => Observable<T>
  ): Observable<T> {
    let cached = this.metadataCache.get(url) as Observable<T> | undefined;
    if (!cached) {
      cached = request().pipe(
        catchError(error => {
          this.metadataCache.delete(url);
          return throwError(() => error);
        }),
        shareReplay(1)
      );
      this.metadataCache.set(url, cached);
    }
    return cached;
  }

  public getAnimationAssetState(
    theme: THEMES,
    type: BackgroundAnimationImageType
  ): Observable<BackgroundAnimationAssetStateResponse> {
    const url = this.getAnimationUrl(theme, type);
    return this.getCached(url, () =>
      this.httpClient.get<BackgroundAnimationResponse>(url, {
        headers: {
          [HttpErrorSkip.HEADER_KEY]: new HttpErrorSkip({
            skipStatusCodes: [HttpStatusCode.NotFound],
          }).parseToString(),
        },
      })
    ).pipe(
      map(response => ({
        hasImageAsset: !!response?.hasImageAsset,
        hasAnimationAsset: !!response?.hasAnimationAsset,
        mode: this.resolveMode(response),
        animation: this.mapResponse(response),
      })),
      catchError(() =>
        of({
          hasImageAsset: false,
          hasAnimationAsset: false,
          mode: 'image' as const,
          animation: null,
        })
      )
    );
  }

  public resolveAsset(
    theme: THEMES,
    type: BackgroundAnimationImageType
  ): Observable<BackgroundAsset> {
    return this.getAnimationAssetState(theme, type).pipe(
      switchMap(({ mode, hasImageAsset, hasAnimationAsset, animation }) => {
        if (mode === 'animation') {
          return of<BackgroundAsset>(
            hasAnimationAsset && animation
              ? { type: BackgroundAssetType.Animation, animation }
              : NONE_BACKGROUND_ASSET
          );
        }

        if (!hasImageAsset) {
          return of(NONE_BACKGROUND_ASSET);
        }

        return this.getBrandingFileKind(theme, type).pipe(
          map(kind => {
            const asset: BackgroundAsset =
              kind === FileKind.VIDEO
                ? {
                    type: BackgroundAssetType.Video,
                    url: this.getMetadataUrl(theme, type),
                  }
                : {
                    type: BackgroundAssetType.Image,
                    url: this.getCacheBustedImageUrl(theme, type),
                  };
            return asset;
          })
        );
      })
    );
  }

  public resolveLayoutBackground(
    theme: THEMES,
    layoutId: number
  ): Observable<BackgroundAsset> {
    return this.resolveEntityBackground(
      theme,
      `${this.orApiUrl}${API_URL.layout}/${layoutId}/background`,
      BrandingFileRole.BACKGROUND
    );
  }

  public resolveLandingBackground(
    theme: THEMES,
    landingId: number
  ): Observable<BackgroundAsset> {
    return this.resolveEntityBackground(
      theme,
      `${this.orApiUrl}${API_URL.landing}/${landingId}/background`,
      BrandingFileRole.LANDING
    );
  }

  private resolveEntityBackground(
    theme: THEMES,
    url: string,
    fallbackRole: BackgroundAnimationImageType
  ): Observable<BackgroundAsset> {
    return this.getCached(url, () =>
      this.httpClient.get<BackgroundFileResponse[]>(url, {
        withCredentials: true,
        headers: {
          [HttpErrorSkip.HEADER_KEY]: new HttpErrorSkip({
            skipStatusCodes: [HttpStatusCode.NotFound],
          }).parseToString(),
        },
      })
    ).pipe(
      switchMap(files => {
        const match = (files ?? []).find(
          file =>
            !!file.fileId && file.theme?.toLowerCase() === theme.toLowerCase()
        );
        if (!match) {
          return this.resolveAsset(theme, fallbackRole);
        }
        const fileUrl = this.fileUrlService.getFileUrl({
          fileId: match.fileId,
          content: match.kind !== FileKind.VIDEO,
        });
        const asset: BackgroundAsset =
          match.kind === FileKind.VIDEO
            ? { type: BackgroundAssetType.Video, url: fileUrl }
            : { type: BackgroundAssetType.Image, url: fileUrl };
        return of(asset);
      }),
      catchError(() => this.resolveAsset(theme, fallbackRole))
    );
  }

  private getBrandingFileKind(
    theme: THEMES,
    type: BackgroundAnimationImageType
  ): Observable<FileKind> {
    const metadataUrl = this.fileUrlService.getFileUrl({
      entity: FileEntity.BRANDING,
      role: type,
      branding: theme,
      content: false,
    });

    return this.getCached(metadataUrl, () =>
      this.httpClient.get<BrandingFileMetadataResponse>(metadataUrl, {
        headers: {
          [HttpErrorSkip.HEADER_KEY]: new HttpErrorSkip({
            skipStatusCodes: [HttpStatusCode.NotFound],
          }).parseToString(),
        },
      })
    ).pipe(
      map(response =>
        response?.kind === FileKind.VIDEO ? FileKind.VIDEO : FileKind.IMAGE
      ),
      catchError(() => of(FileKind.IMAGE))
    );
  }

  private getAnimationUrl(
    theme: THEMES,
    type: BackgroundAnimationImageType
  ): string {
    return `${this.baseUrl}${OD_BACKEND_ROUTES.backgroundAnimations}/${type}-${theme}`;
  }

  private getCacheBustedImageUrl(
    theme: THEMES,
    type: BackgroundAnimationImageType
  ): string {
    return this.fileUrlService.getFileUrl({
      entity: FileEntity.BRANDING,
      role: type,
      branding: theme,
      content: true,
    });
  }

  private getMetadataUrl(
    theme: THEMES,
    type: BackgroundAnimationImageType
  ): string {
    return this.fileUrlService.getFileUrl({
      entity: FileEntity.BRANDING,
      role: type,
      branding: theme,
      content: false,
    });
  }

  private mapResponse(
    response: BackgroundAnimationResponse
  ): BackgroundAnimationAsset | null {
    const html = typeof response?.html === 'string' ? response.html.trim() : '';
    const css = typeof response?.css === 'string' ? response.css.trim() : '';

    if (!html && !css) {
      return null;
    }

    return {
      html,
      css,
    };
  }

  private resolveMode(
    response: BackgroundAnimationResponse
  ): BackgroundAssetMode {
    return response?.mode === 'animation' ? 'animation' : 'image';
  }
}
